import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Sparkles, 
  BookOpen, 
  Target, 
  Star, 
  Wrench, 
  Clock, 
  User,
  Calendar,
  ChevronRight,
  Search,
  Filter,
  TrendingUp,
  Lightbulb,
  Globe,
  Atom,
  Calculator,
  Microscope,
  Palette,
  Book,
  Users,
  Shield,
  PlayCircle,
  Brain,
  History,
  Zap,
  Award,
  MapPin,
  Briefcase,
  GraduationCap,
  TrendingDown,
  AlertCircle,
  ChevronLeft,
  ExternalLink,
  Quote,
  UserCircle,
  Layers,
  Rocket,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/components/auth/AuthProvider';
import { sparksService, type SparkSummary, type SparkDetail, subjectMeta, type SubjectMeta, type TimelineEvent, type Personality, type Subdomain, type Specialisation } from '@/services/sparks-service';
import { useSparksCache } from '@/hooks/use-sparks-cache';

const iconMap = {
  Calculator,
  Atom,
  Microscope,
  Book,
  Clock,
  Globe,
  Users,
  Shield,
};

const categories = ["All", "STEM", "Languages", "Social Studies", "Applied Sciences"];

const SparksPage: React.FC = () => {
  const { user, session } = useAuth();
  const isAuthenticated = user && session;
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  
  // Use sparks cache instead of local state
  const {
    sparks: sparksData,
    hasLoaded,
    isLoading: loading,
    detailLoading,
    error,
    fetchSparks,
    fetchSparkDetail,
    getCachedDetail
  } = useSparksCache();
  
  const [selectedSparkDetail, setSelectedSparkDetail] = useState<SparkDetail | null>(null);

  // Fetch sparks list on component mount
  useEffect(() => {
    if (!hasLoaded && !loading) {
      fetchSparks().catch(error => {
        console.error('Failed to load sparks:', error);
      });
    }
  }, [hasLoaded, loading, fetchSparks]);

  // Fetch detailed spark data when a subject is selected
  useEffect(() => {
    const fetchDetail = async () => {
      if (!selectedSubject) {
        setSelectedSparkDetail(null);
        return;
      }

      try {
        const response = await fetchSparkDetail(selectedSubject);
        setSelectedSparkDetail(response);
      } catch (err) {
        console.error('Error fetching spark detail:', err);
        // Error handling is done in the hook
      }
    };

    fetchDetail();
  }, [selectedSubject, fetchSparkDetail]);

  const filteredSubjects = sparksData.filter((spark) => {
    const subjectKey = sparksService.subjectNameToKey(spark.subject_name);
    const meta = subjectMeta[subjectKey];
    
    if (!meta) return false; // Skip subjects without metadata
    
    const matchesSearch = spark.subject_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || meta.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const SubjectCard = ({ spark }: { spark: SparkSummary }) => {
    const subjectKey = sparksService.subjectNameToKey(spark.subject_name);
    const meta = subjectMeta[subjectKey];
    
    if (!meta) return null; // Skip subjects without metadata
    
    const IconComponent = iconMap[meta.icon as keyof typeof iconMap];
    
    return (
      <Card 
        className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-0 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 hover:scale-[1.02]"
        onClick={() => setSelectedSubject(spark._id)}
      >
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className={`p-3 rounded-xl bg-gradient-to-r ${meta.color} shadow-lg group-hover:shadow-xl transition-shadow`}>
              <IconComponent className="h-6 w-6 text-white" />
            </div>
            <Badge variant="outline" className="text-xs">
              {meta.category}
            </Badge>
          </div>
          <CardTitle className="text-xl font-bold group-hover:text-primary transition-colors">
            {spark.subject_name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
            {spark.introduction}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex gap-3">
                <div className="flex items-center gap-1">
                    <Target className="h-3 w-3 text-blue-500" />
                    <span className="text-xs text-muted-foreground">Purpose</span>
                </div>
                <div className="flex items-center gap-1">
                    <Star className="h-3 w-3 text-yellow-500" />
                    <span className="text-xs text-muted-foreground">Importance</span>
                </div>
                <div className="flex items-center gap-1">
                    <Briefcase className="h-3 w-3 text-orange-500" />
                    <span className="text-xs text-muted-foreground">Careers</span>
                </div>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </CardContent>
      </Card>
    );
  };

  const TimelineComponent = ({ events }: { events: TimelineEvent[] }) => {
    if (!events || events.length === 0) return null;

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <History className="h-5 w-5 text-purple-500" />
          Historical Timeline
        </h3>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-500 to-blue-500"></div>
          
          {events.map((event, index) => (
            <div key={index} className="relative flex items-start gap-4 pb-8">
              {/* Timeline dot */}
              <div className="relative z-10 flex-shrink-0 w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
                <div className="w-3 h-3 bg-white rounded-full"></div>
              </div>
              
              {/* Event content */}
              <div className="flex-1 pt-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg border p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="text-sm font-semibold text-purple-600 dark:text-purple-400 mb-1">
                    {event.year}
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    {event.event}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const PersonalityCard = ({ personality }: { personality: Personality }) => (
    <Card className="h-full hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-2 rounded-full bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/20 dark:to-blue-900/20">
            <UserCircle className="h-5 w-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-sm mb-2">{personality.name}</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">{personality.contributions}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const SubdomainCard = ({ subdomain }: { subdomain: Subdomain }) => (
    <Card className="h-full hover:shadow-lg transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/20 dark:to-emerald-900/20">
            <Layers className="h-4 w-4 text-green-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-sm mb-2">{subdomain.name}</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">{subdomain.explaination}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const SpecialisationCard = ({ specialisation }: { specialisation: Specialisation }) => (
    <Card className="h-full hover:shadow-lg transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-r from-orange-100 to-red-100 dark:from-orange-900/20 dark:to-red-900/20">
            <Briefcase className="h-4 w-4 text-orange-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-sm mb-2">{specialisation.name}</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">{specialisation.applications}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const SubjectDetail = ({ sparkDetail }: { sparkDetail: SparkDetail }) => {
    const subjectKey = sparksService.subjectNameToKey(sparkDetail.subject_name);
    const meta = subjectMeta[subjectKey];
    
    if (!meta) return null;
    
    const IconComponent = iconMap[meta.icon as keyof typeof iconMap];
    
    return (
      <div className="space-y-8">
        {/* Enhanced Header with Navigation */}
        <div className="relative">
          <div className="absolute top-4 left-4 z-50">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setSelectedSubject(null)}
              className="hover:bg-white/20 backdrop-blur-sm text-white border-white/30 hover:border-white/50"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Subjects
            </Button>
          </div>
          
          <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${meta.color} p-12 text-center text-white`}>
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10 space-y-6">
              <div className="inline-flex p-6 rounded-full bg-white/20 backdrop-blur-sm shadow-xl">
                <IconComponent className="h-16 w-16" />
              </div>
              <div>
                <h1 className="text-5xl font-bold mb-2">{sparkDetail.subject_name}</h1>
                <p className="text-xl opacity-90 max-w-3xl mx-auto leading-relaxed">
                  {sparkDetail.introduction}
                </p>
              </div>
              <div className="flex items-center justify-center gap-4">
                <Badge variant="secondary" className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                  {meta.category}
                </Badge>
                {sparkDetail.timeline && (
                  <Badge variant="secondary" className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                    <History className="h-3 w-3 mr-1" />
                    {sparkDetail.timeline.length} Historical Events
                  </Badge>
                )}
                {sparkDetail.personalities && (
                  <Badge variant="secondary" className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                    <UserCircle className="h-3 w-3 mr-1" />
                    {sparkDetail.personalities.length} Key Figures
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {/* {isAuthenticated && (
          <div className="flex gap-4 justify-center">
            <Button asChild size="lg" className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
              <Link to={`/sparks/${subjectKey}/learn`}>
                <GraduationCap className="h-4 w-4 mr-2" />
                Start Learning Journey
              </Link>
            </Button>
            <Button variant="outline" size="lg">
              <Users className="h-4 w-4 mr-2" />
              Join Study Group
            </Button>
            <Button variant="outline" size="lg">
              <BookOpen className="h-4 w-4 mr-2" />
              Download Resources
            </Button>
          </div>
        )} */}

        {/* Enhanced Content Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-8 h-12">
            <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
            <TabsTrigger value="purpose" className="text-xs">Purpose</TabsTrigger>
            <TabsTrigger value="importance" className="text-xs">Importance</TabsTrigger>
            <TabsTrigger value="storytelling" className="text-xs">Story</TabsTrigger>
            <TabsTrigger value="impact" className="text-xs">World Impact</TabsTrigger>
            <TabsTrigger value="personalities" className="text-xs">Key Figures</TabsTrigger>
            <TabsTrigger value="domains" className="text-xs">Domains</TabsTrigger>
            <TabsTrigger value="timeline" className="text-xs">Timeline</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="space-y-6 mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-yellow-500" />
                    What You'll Learn
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg leading-relaxed mb-6">{sparkDetail.purpose}</p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Target className="h-5 w-5 text-blue-500 mt-1" />
                      <div>
                        <h4 className="font-semibold mb-1">Learning Objectives</h4>
                        <p className="text-sm text-muted-foreground">Develop critical thinking and analytical skills specific to {sparkDetail.subject_name}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Wrench className="h-5 w-5 text-green-500 mt-1" />
                      <div>
                        <h4 className="font-semibold mb-1">Practical Skills</h4>
                        <p className="text-sm text-muted-foreground">{sparkDetail.utility}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-500" />
                    Quick Stats
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{sparkDetail.timeline?.length || 0}</div>
                    <p className="text-sm text-muted-foreground">Historical Milestones</p>
                  </div>
                  <Separator />
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600">{sparkDetail.personalities?.length || 0}</div>
                    <p className="text-sm text-muted-foreground">Notable Figures</p>
                  </div>
                  <Separator />
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{sparkDetail.subdomains?.length || 0}</div>
                    <p className="text-sm text-muted-foreground">Sub-disciplines</p>
                  </div>
                  <Separator />
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-600">{sparkDetail.specialisations?.length || 0}</div>
                    <p className="text-sm text-muted-foreground">Career Paths</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="purpose" className="space-y-6 mt-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-6 w-6 text-blue-500" />
                  Learning Purpose & Objectives
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg leading-relaxed">{sparkDetail.purpose}</p>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="importance" className="space-y-6 mt-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-6 w-6 text-yellow-500" />
                  Why This Subject Matters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg leading-relaxed mb-6">{sparkDetail.importance}</p>
                
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-6 rounded-lg">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Brain className="h-5 w-5 text-blue-600" />
                    Historical Context
                  </h4>
                  <p className="text-sm leading-relaxed">{sparkDetail.past}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="storytelling" className="space-y-6 mt-8">
            {sparkDetail.storytelling && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Quote className="h-6 w-6 text-purple-500" />
                    Real-World Story
                  </CardTitle>
                  <CardDescription>
                    Discover how {sparkDetail.subject_name} applies to everyday life through engaging narratives
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="prose dark:prose-invert max-w-none">
                    {sparkDetail.storytelling.split('\n\n').map((paragraph: string, index: number) => (
                      <p key={index} className="text-base leading-relaxed mb-4 text-gray-700 dark:text-gray-300">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="impact" className="space-y-6 mt-8">
            {sparkDetail.world_impact && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-6 w-6 text-green-500" />
                      Global Impact & Influence
                    </CardTitle>
                    <CardDescription>
                      Understanding both positive contributions and potential challenges of {sparkDetail.subject_name}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="prose dark:prose-invert max-w-none">
                      {sparkDetail.world_impact.split('\n\n').map((paragraph: string, index: number) => {
                        const isPositive = paragraph.toLowerCase().includes('positive');
                        const isNegative = paragraph.toLowerCase().includes('negative') || paragraph.toLowerCase().includes('however');
                        
                        return (
                          <div key={index} className={`p-4 rounded-lg mb-4 ${
                            isPositive 
                              ? 'bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500' 
                              : isNegative 
                                ? 'bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500'
                                : 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
                          }`}>
                            <div className="flex items-start gap-2">
                              {isPositive && <TrendingUp className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />}
                              {isNegative && <AlertCircle className="h-5 w-5 text-red-600 mt-1 flex-shrink-0" />}
                              {!isPositive && !isNegative && <Globe className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />}
                              <p className="text-sm leading-relaxed">{paragraph}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="personalities" className="space-y-6 mt-8">
            {sparkDetail.personalities && sparkDetail.personalities.length > 0 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-2">Notable Figures in {sparkDetail.subject_name}</h3>
                  <p className="text-muted-foreground">Pioneers and innovators who shaped this field</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sparkDetail.personalities.map((personality, index) => (
                    <PersonalityCard key={index} personality={personality} />
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="domains" className="space-y-6 mt-8">
            <div className="space-y-8">
              {sparkDetail.subdomains && sparkDetail.subdomains.length > 0 && (
                <div>
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold mb-2 flex items-center justify-center gap-2">
                      <Layers className="h-6 w-6 text-green-600" />
                      Core Areas of Study
                    </h3>
                    <p className="text-muted-foreground">Key sub-disciplines within {sparkDetail.subject_name}</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sparkDetail.subdomains.map((subdomain, index) => (
                      <SubdomainCard key={index} subdomain={subdomain} />
                    ))}
                  </div>
                </div>
              )}

              {sparkDetail.specialisations && sparkDetail.specialisations.length > 0 && (
                <div>
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold mb-2 flex items-center justify-center gap-2">
                      <Briefcase className="h-6 w-6 text-orange-600" />
                      Career Specializations
                    </h3>
                    <p className="text-muted-foreground">Professional applications and career paths</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sparkDetail.specialisations.map((specialisation, index) => (
                      <SpecialisationCard key={index} specialisation={specialisation} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="timeline" className="space-y-6 mt-8">
            <Card>
              <CardContent className="pt-6">
                <TimelineComponent events={sparkDetail.timeline} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    );
  };

  // Show detailed view if subject is selected and detail is loaded
  if (selectedSubject && selectedSparkDetail && !detailLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4 py-8">
          <SubjectDetail sparkDetail={selectedSparkDetail} />
        </div>
      </div>
    );
  }

  // Show loading state for detail view
  if (selectedSubject && detailLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground">Loading subject details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center space-y-6 mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-full">
            <Sparkles className="h-5 w-5 text-purple-600" />
            <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
              Ignite Your Learning Journey
            </span>
          </div>
          
          <h1 className="text-5xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent dark:from-white dark:via-gray-200 dark:to-white">
            Subject Sparks
          </h1>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Discover the fascinating world of knowledge through interactive subject explorations. 
            Each subject is a spark that can ignite your passion for learning and understanding.
          </p>

          {!isAuthenticated && (
            <div className="flex gap-4 justify-center">
              <Button asChild size="lg" className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
                <Link to="/signup">
                  <Zap className="h-4 w-4 mr-2" />
                  Start Your Journey
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/login">Login</Link>
              </Button>
            </div>
          )}
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <Card className="text-center border-0 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20">
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-blue-600">{sparksData.length}</div>
              <p className="text-sm text-muted-foreground">Subject Areas</p>
            </CardContent>
          </Card>
          <Card className="text-center border-0 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20">
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-green-600">{categories.length - 1}</div>
              <p className="text-sm text-muted-foreground">Categories</p>
            </CardContent>
          </Card>
          <Card className="text-center border-0 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20">
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-purple-600">∞</div>
              <p className="text-sm text-muted-foreground">Learning Paths</p>
            </CardContent>
          </Card>
          <Card className="text-center border-0 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20">
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-orange-600">100%</div>
              <p className="text-sm text-muted-foreground">Interactive</p>
            </CardContent>
          </Card>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground">Loading subjects...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center space-y-4">
              <AlertCircle className="h-8 w-8 mx-auto text-red-500" />
              <p className="text-red-600">{error}</p>
              <Button onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </div>
          </div>
        )}

        {/* Search and Filter */}
        {!loading && !error && (
          <>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search subjects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>

            {/* Subject Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSubjects.map((spark) => (
                <SubjectCard key={spark._id} spark={spark} />
              ))}
            </div>

            {filteredSubjects.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No subjects found matching your criteria.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SparksPage;
